import { LogGaEvent } from "lib/GoogleAnalytics"
import { EventAction, EventCategory } from "models/GoogleAnalytics"
import { Status } from "pages/Application/Status/Status"
import React, { useState } from "react"
import styled from "styled-components"
import devices from "styles/devices"
import { Sidebar } from "./Sidebar"
import HamburgerIcon from "url:assets/images/icons/Menu.svg"
import CloseHamburgerIcon from "url:assets/images/icons/Cross.svg"
import { useApplicationFriendlyId } from "hooks/useApplication"
import { useApplicationSummaryEnhanced } from "hooks/useApplicationSummaryEnhanced"
import { OpportunityStage } from "models/FullApplication"
import { Documents } from "pages/Application/Status/Documents"
import { RateCheckerPage } from "pages/Application/Status/RateCheckPromise/RateCheckerPage"
import { RecommendationWrapper } from "pages/Application/Recommendation/Recommendation"
import BackgroundWave from "templates/BackgroundWave"
import { ConveyancingPage } from "pages/Application/Status/Conveyancing/ConveyancingPage"
import useRateCheck from "hooks/useRateCheck"
import { OptedOut } from "pages/RateCheckPromise/OptedOut"
import { Loader, LoaderTheme } from "components/atoms/Loader"
import { useApplicationSummaries } from "hooks/useApplicationSummaries"
import { getConfigFromApplication } from "lib/GetClientConfig"
import { PaymentConfirmedPage } from "./PaymentConfirmed"

const Page = styled.div({
  display: "flex",
  position: "relative",
  flexDirection: "column",
  [`@media screen and ${devices.bp_xl}`]: {
    flexDirection: "row",
  },
})

const MobileNav = styled.div({
  height: "56px",
  background: "white",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "0 16px",
  [`@media screen and ${devices.bp_xl}`]: {
    display: "none",
  },
})

const Hamburger = ({ isOpen, onClick }) => (
  <div onClick={onClick}>
    <img src={isOpen ? CloseHamburgerIcon : HamburgerIcon} alt="nav icon" />
  </div>
)

export enum StatusPageNavSelected {
  Status = "Status",
  Recommendation = "Recommendation",
  RateChecker = "Rate Checker",
  Conveyancing = "Conveyancing",
  Documents = "Documents",
}

interface StatusIndexPageProps {
  friendlyId: string
  opportunityStage: OpportunityStage
  address?: string
  selected?: StatusPageNavSelected
  servicePaid?: boolean
}

const CenteredLoader = () => (
  <div style={{ display: "flex", justifyContent: "center" }}>
    <Loader size={100} theme={LoaderTheme.Outline} />
  </div>
)

export const StatusPage = ({
  friendlyId,
  opportunityStage,
  address = "",
  selected = StatusPageNavSelected.Status,
  servicePaid = false,
}: StatusIndexPageProps) => {
  const { application: salesforceApplication } = useApplicationSummaryEnhanced({ friendlyId })
  const { refreshApplications } = useApplicationSummaries()
  const { application: dynamoApplication } = useApplicationFriendlyId(friendlyId)
  const [isOpen, setIsOpen] = useState(false)
  const isOptedIntoRateCheckPromise = useRateCheck(friendlyId)
  const { logo, serviceType } = getConfigFromApplication(salesforceApplication)

  return (
    <Page>
      <BackgroundWave />
      <MobileNav>
        <img src={logo} alt="mojo logo" style={{ height: "28px" }} />
        <Hamburger
          isOpen={isOpen}
          onClick={() => {
            LogGaEvent({
              action: EventAction.navigation,
              event_category: EventCategory.functionalInteraction,
              event_label: isOpen ? "Close" : "Open",
            })
            setIsOpen(!isOpen)
          }}
        />
      </MobileNav>
      <Sidebar
        friendlyId={friendlyId}
        address={address}
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        selected={selected}
        mojoLogo={logo}
      />
      {servicePaid ? (
        <PaymentConfirmedPage />
      ) : (
        <>
          {selected === StatusPageNavSelected.Status && (
            <Status
              serviceType={serviceType}
              friendlyId={friendlyId}
              opportunityStage={opportunityStage}
              salesforceApplication={salesforceApplication}
              application={dynamoApplication}
              refreshApplications={refreshApplications}
            />
          )}
          {selected === StatusPageNavSelected.Recommendation && (
            <RecommendationWrapper
              serviceType={serviceType}
              applicationId={salesforceApplication?.applicationId}
              friendlyId={salesforceApplication?.friendlyId}
            />
          )}
          {selected === StatusPageNavSelected.RateChecker &&
            (isOptedIntoRateCheckPromise === null ? ( // null means it hasn't been fetched yet
              <CenteredLoader />
            ) : isOptedIntoRateCheckPromise ? (
              <RateCheckerPage applicationId={salesforceApplication?.applicationId} />
            ) : (
              <OptedOut />
            ))}
          {selected === StatusPageNavSelected.Conveyancing && <ConveyancingPage />}
          {selected === StatusPageNavSelected.Documents && (
            <Documents friendlyId={friendlyId} application={dynamoApplication} />
          )}
        </>
      )}
    </Page>
  )
}
